import {
  Menu,
  UnstyledButton,
  Group,
  Text,
  Badge,
  Button,
  ThemeIcon,
} from "@mantine/core";
import {
  IconSelector,
  IconBuilding,
  IconUser,
  IconPlus,
  IconCheck,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../contexts/WorkspaceContext";
import type { WorkspaceWithMembership } from "../lib/types";

interface WorkspaceSwitcherProps {
  onCreateClick: () => void;
}

function WorkspaceItem({
  workspace,
  isActive,
}: {
  workspace: WorkspaceWithMembership;
  isActive: boolean;
}) {
  const isPersonal = workspace.type === "personal";
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon
        size="sm"
        variant="light"
        color={isPersonal ? "blue" : "violet"}
        radius="sm"
      >
        {isPersonal ? <IconUser size={12} /> : <IconBuilding size={12} />}
      </ThemeIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" truncate>
          {workspace.name}
        </Text>
      </div>
      {isActive && <IconCheck size={14} style={{ flexShrink: 0 }} />}
    </Group>
  );
}

const FREE_TIER_MAX_WORKSPACES = 3;

export function WorkspaceSwitcher({ onCreateClick }: WorkspaceSwitcherProps) {
  const { workspaces, activeWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const ownedCount = workspaces.filter((ws) => ws.role === "owner").length;
  const atLimit = ownedCount >= FREE_TIER_MAX_WORKSPACES;

  function switchWorkspace(workspaceId: string) {
    localStorage.setItem("showrunner:lastWorkspaceId", workspaceId);
    navigate(`/${workspaceId}/prompts`);
  }

  return (
    <Menu shadow="md" width={240} position="bottom-start">
      <Menu.Target>
        <UnstyledButton
          px="xs"
          py={6}
          style={(theme) => ({
            borderRadius: theme.radius.sm,
            width: "100%",
            "&:hover": { backgroundColor: theme.colors.gray[0] },
          })}
        >
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              <ThemeIcon
                size="sm"
                variant="light"
                color={activeWorkspace?.type === "personal" ? "blue" : "violet"}
                radius="sm"
              >
                {activeWorkspace?.type === "personal" ? (
                  <IconUser size={12} />
                ) : (
                  <IconBuilding size={12} />
                )}
              </ThemeIcon>
              <Text size="sm" fw={600} truncate>
                {activeWorkspace?.name ?? "Select workspace"}
              </Text>
              {activeWorkspace && (
                <Badge size="xs" variant="light" color="gray">
                  {activeWorkspace.tier}
                </Badge>
              )}
            </Group>
            <IconSelector size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Workspaces</Menu.Label>
        {workspaces.map((ws) => (
          <Menu.Item
            key={ws.id}
            onClick={() => switchWorkspace(ws.id)}
          >
            <WorkspaceItem
              workspace={ws}
              isActive={ws.id === activeWorkspace?.id}
            />
          </Menu.Item>
        ))}
        <Menu.Divider />
        {atLimit ? (
          <>
            <Menu.Item
              leftSection={<IconPlus size={14} />}
              disabled
            >
              Create workspace
            </Menu.Item>
            <Group px="sm" pb={6} pt={2} gap="xs" align="center">
              <Text size="xs" c="dimmed">
                Free plan limit
              </Text>
              <Button
                size="compact-xs"
                variant="light"
                radius="xl"
                onClick={() => navigate(`/${activeWorkspace?.id}/settings`)}
              >
                Upgrade
              </Button>
            </Group>
          </>
        ) : (
          <Menu.Item
            leftSection={<IconPlus size={14} />}
            onClick={onCreateClick}
          >
            Create workspace
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
